/**
 * ComonadExplainer — progressive-disclosure explainer for comonads.
 *
 * A comonad `(W, ε, δ)` is the categorical dual of a monad. Where a monad
 * describes "how an effectful step composes", a comonad describes "what a
 * step needs from its context to run". CSK's REQUIRE is its comonadic
 * dual to PRECOMMIT.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { COMPOSITIONALITY_PRINCIPLE_COLOR } from "../../design-system/tokens.js";
import { MathBlock, MathInline, tex } from "../../components/math/index.js";
import {
  ComonadDiagram,
  ExplainerCard,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";

const ACCENT = COMPOSITIONALITY_PRINCIPLE_COLOR.REQUIRE.color;

const IDENTITY: ExplainerIdentity = {
  id: "explainer-comonad",
  kind: "Categorical structure",
  title: "Comonad",
  subtitle: "(W, ε : W ⇒ Id, δ : W ⇒ W ∘ W)",
  accent: ACCENT,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  { label: "Monad", href: "#explainer-monad" },
  { label: "Graded monad", href: "#explainer-graded-monad" },
  { label: "Bimonad", href: "#explainer-bimonad" },
  { label: "Adjunction", href: "#explainer-adjunction" },
  { label: "REQUIRE (At)", href: "#explainer-bimonad", accent: ACCENT },
];

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      A <strong>comonad</strong> is a monad with the arrows flipped: a context-providing wrapper{" "}
      <MathInline>{tex`W`}</MathInline> with rules for <em>extracting</em> a value (
      <MathInline>{tex`\varepsilon`}</MathInline>) and for <em>duplicating</em> the context one
      layer deeper (<MathInline>{tex`\delta`}</MathInline>). CSK uses comonads to describe what a
      step <em>requires</em> from its surrounding environment.
    </p>
  ),
  intuition: (
    <>
      <p>
        A monad asks "what effect will I leave behind?" A comonad asks "what context do I need to
        run?". Where a monad pushes a value into an envelope and discharges later, a comonad starts
        with a value already living inside its envelope and lets you extract a focused view.
      </p>
      <p>Three everyday examples each give one different context:</p>
      <ul>
        <li>
          <code>Env e</code> — "I run with read-only environment <code>e</code> on hand."{" "}
          <MathInline>{tex`\varepsilon`}</MathInline> projects out the value;{" "}
          <MathInline>{tex`\delta`}</MathInline> duplicates the environment record so a sub-step can
          read the same environment.
        </li>
        <li>
          <code>Store s</code> — "I am a value at a position <code>s</code>."{" "}
          <MathInline>{tex`\varepsilon`}</MathInline> reads the value at the current position;{" "}
          <MathInline>{tex`\delta`}</MathInline> hands you a re-positionable version so neighboring
          positions can be queried.
        </li>
        <li>
          <code>Stream</code> — "I have an infinite history of values to my left."{" "}
          <MathInline>{tex`\varepsilon`}</MathInline> reads the current value;{" "}
          <MathInline>{tex`\delta`}</MathInline> produces a stream-of-streams so every prefix is
          addressable.
        </li>
      </ul>
      <p>
        Just as monadic <code>{">>="}</code> threads effects forward, <em>co-Kleisli</em>{" "}
        <code>{"=>>"}</code> (extend) threads requirements <em>backward</em>: each step sees the
        full context the next step would have needed.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        Let <MathInline>{tex`C`}</MathInline> be a category. A <strong>comonad</strong> on{" "}
        <MathInline>{tex`C`}</MathInline> is an endofunctor{" "}
        <MathInline>{tex`W : C \to C`}</MathInline> with two natural transformations:
      </p>
      <ul>
        <li>
          <strong>Counit</strong>{" "}
          <MathInline>{tex`\varepsilon : W \Rightarrow \mathrm{Id}`}</MathInline> — for every{" "}
          <MathInline>{tex`A`}</MathInline> a map{" "}
          <MathInline>{tex`\varepsilon_{A} : W(A) \to A`}</MathInline> (extract).
        </li>
        <li>
          <strong>Comultiplication</strong>{" "}
          <MathInline>{tex`\delta : W \Rightarrow W \circ W`}</MathInline> — for every{" "}
          <MathInline>{tex`A`}</MathInline> a map{" "}
          <MathInline>{tex`\delta_{A} : W(A) \to W(W(A))`}</MathInline> (duplicate).
        </li>
      </ul>
      <ComonadDiagram accent={ACCENT} />
      <p style={{ marginTop: "0.5rem" }}>Subject to the dual coherence laws:</p>
      <ul>
        <li>
          <strong>Left counit:</strong>{" "}
          <MathInline>{tex`\varepsilon W \circ \delta = \mathrm{id}_{W}`}</MathInline>
        </li>
        <li>
          <strong>Right counit:</strong>{" "}
          <MathInline>{tex`W \varepsilon \circ \delta = \mathrm{id}_{W}`}</MathInline>
        </li>
        <li>
          <strong>Coassociativity:</strong>{" "}
          <MathInline>{tex`\delta W \circ \delta = W \delta \circ \delta`}</MathInline>
        </li>
      </ul>
      <p style={{ marginTop: "0.75rem" }}>
        A <strong>co-Kleisli</strong> arrow from <MathInline>{tex`A`}</MathInline> to{" "}
        <MathInline>{tex`B`}</MathInline> is an ordinary morphism{" "}
        <MathInline>{tex`f : W(A) \to B`}</MathInline>. Co-Kleisli composition is
      </p>
      <MathBlock ariaLabel="g ∘_W f = g ∘ W(f) ∘ δ">
        {tex`g \circ_{W} f \;=\; g \circ W(f) \circ \delta`}
      </MathBlock>
      <p>
        and produces the <strong>co-Kleisli category</strong> of <MathInline>{tex`W`}</MathInline> —
        the venue where "context-needing" computations compose. Every adjunction{" "}
        <MathInline>{tex`F \dashv G`}</MathInline> simultaneously produces a monad{" "}
        <MathInline>{tex`G \circ F`}</MathInline> on the left category and a comonad{" "}
        <MathInline>{tex`F \circ G`}</MathInline> on the right; the unit and counit of the
        adjunction become the unit/counit of the monad and the counit/comultiplication of the
        comonad.
      </p>
    </>
  ),
  relevance: (
    <>
      <p>
        Comonads are how CSK talks about <em>requirements</em> — what evidence must be on hand
        before a step is allowed to run. Three concrete payoffs:
      </p>
      <ul>
        <li>
          <strong>Requirements are typed contexts.</strong> "This step needs an authorization
          witness" becomes "this step is a co-Kleisli arrow on <code>Auth-W</code>". Forgetting the
          witness is a type error, not a runtime check.
        </li>
        <li>
          <strong>
            Discharge is exactly <MathInline>{tex`\varepsilon`}</MathInline>.
          </strong>{" "}
          The counit is the only canonical way to drop the context and keep the value. Anything that
          "discards a requirement implicitly" must, by construction, factor through{" "}
          <MathInline>{tex`\varepsilon`}</MathInline> — and that factoring is the audit trail.
        </li>
        <li>
          <strong>
            <code>REQUIRE</code> is the dual SDP of <code>PRECOMMIT</code>.
          </strong>{" "}
          Where <code>PRECOMMIT</code> is a graded <em>monad</em> tracking commitments accumulated,{" "}
          <code>REQUIRE</code> is the graded <em>comonad</em> tracking obligations still owed. The
          pair is recorded in the canon as <code>ICG-At</code> (
          <MathInline>{tex`\mathrm{monad} \dashv \mathrm{comonad}`}</MathInline> adjunction).
        </li>
      </ul>
      <p>
        Read in this light, a comonad is the smallest piece of structure that lets a program{" "}
        <em>say</em> "I need this evidence on hand before I will run" — and have the type system
        enforce that the evidence is genuinely supplied, never assumed.
      </p>
    </>
  ),
  references: [
    {
      label: "Monads-And-Algebras",
      path: "theory/type-theory/Monads-And-Algebras.md",
      note: "Comonad definition, co-Kleisli category, dual Eilenberg–Moore theory.",
    },
    {
      label: "Comonad (MathLib, Agda)",
      path: "agda/MathLib/Monad/Comonad.agda",
    },
    {
      label: "REQUIRE comonad duality (Agda)",
      path: "agda/Kernel/CompositionalityPrinciple/REQUIRE/ComonadDuality.agda",
      note: "Mechanized statement that REQUIRE is the comonadic dual SDP of PRECOMMIT.",
    },
    {
      label: "Theorem Canon (REQUIRE / ICG-At)",
      path: "docs/reference/Theorem-Canon.md",
    },
  ],
};

export function ComonadExplainer(): React.JSX.Element {
  return (
    <ExplainerCard
      identity={IDENTITY}
      tiers={TIERS}
      diagram={<ComonadDiagram accent={ACCENT} />}
      crossLinks={CROSS_LINKS}
    />
  );
}
